import { parseDigit, templateFormatter, templateParser } from "input-format";
import ReactInput from "input-format/react";
import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import ColoredBadge from "../../components/ColoredBadge";
import Header from "../../components/Header";
import PageContainer from "../../components/PageContainer";
import Sidebar from "../../components/Sidebar";
import { formatTaxNumber } from "../../helpers/formatTaxNumber";
import { getNewLoggedInEmployeeFunction } from "../../helpers/getLoggedInEmployeeFunction";
import { TAX_NUMBER_REGEX } from "../../helpers/taxNumberRegex";
import { getClient } from "../../services/getClient";
import { getTeams } from "../../services/teams/getTeams";
import { updateClient } from "../../services/updateClient";
import AssignTeamsListBox from "../Teams/AssignTeamsListBox";

function CreateClientForm() {
    const navigate = useNavigate();
    const params = useParams();
    const [teams, setTeams] = useState([]);
    const [loggedInEmployee, setLoggedInEmployee] = useState({});
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({
        clientName: "",
        phoneNumber: "",
        taxNumber: "",

        isActive: null, // true | false | null
        subscriptionCancellationDate: null, // 'YYYY-MM-DD' | null

        inactiveReason: null,

        address: {
            street: "",
            city: "",
            stateProvince: "",
            postalCode: "",
            country: "United States",
        },
        clientDetail: {
            clientType: "individual",
            ssn: "",
            spouseName: "",
            spouseEmail: "",
            spouseTaxNumber: "",
            pointOfContactName: "",
            pointOfContactEmail: "",
            secondaryPointOfContactName: "",
            secondaryPointOfContactEmail: "",
            clientEmail: "",
            isMeetingRequired: false,
        },
    });

    useEffect(() => {
        getNewLoggedInEmployeeFunction(setLoggedInEmployee);
    }, []);

    // helper para normalizar fechas a 'YYYY-MM-DD'
    const toYYYYMMDD = (d) => {
        if (!d) return null;
        const date = typeof d === "string" ? new Date(d) : d;
        if (Number.isNaN(date.getTime())) return null;
        return date.toISOString().split("T")[0];
    };

    useEffect(() => {
        async function runOnLoad() {
            const client = await getClient(params.id);
            let clientVal = client.data?.clientDetail?.clientType;
            let defaultClientVal =
                clientVal === undefined ? "individual" : clientVal?.toLowerCase();

            setForm({
                id: client.data.id,
                clientName: client.data.clientName,
                phoneNumber: client.data.phoneNumber,
                taxNumber: String(client.data.taxNumber ?? "").replace(/\D/g, ""),
                inactiveReason: client.data?.inactiveReason ?? null,
                isActive:
                    typeof client.data?.isActive === "boolean"
                        ? client.data.isActive
                        : null,
                subscriptionCancellationDate: toYYYYMMDD(
                    client.data?.subscriptionCancellationDate
                ),
                manager: client.data?.manager || null,
                teams: client.data?.teams || [],

                address: {
                    street: client.data.address.street,
                    city: client.data.address.city,
                    stateProvince: client.data.address.stateProvince,
                    postalCode: client.data.address.postalCode,
                    country: client.data.address.country,
                },
                clientDetail: {
                    clientType: defaultClientVal,
                    spouseName: client.data?.clientDetail?.spouseName,
                    spouseEmail: client.data?.clientDetail?.spouseEmail,
                    spouseTaxNumber: String(
                        client.data?.clientDetail?.spouseTaxNumber ?? ""
                    ).replace(/\D/g, ""),
                    pointOfContactName:
                        client.data?.clientDetail?.pointOfContactName,
                    pointOfContactEmail:
                        client.data?.clientDetail?.pointOfContactEmail,
                    secondaryPointOfContactName:
                        client.data?.clientDetail?.secondaryPointOfContactName,
                    secondaryPointOfContactEmail:
                        client.data?.clientDetail?.secondaryPointOfContactName,
                    clientEmail: client.data?.clientDetail?.clientEmail,
                    language: client.data?.clientDetail?.language,
                    contactMethod: client.data?.clientDetail?.contactMethod,
                    isMeetingRequired: client.data.clientDetail.isMeetingRequired,
                },
            });

            setLoading(false);
        }

        const fetchTeams = async () => {
            const response = await getTeams();
            if (response.data.success) setTeams(response.data.teams);
        };

        fetchTeams();
        runOnLoad();
        // eslint-disable-next-line
    }, []);

    async function save() {
        if (loggedInEmployee.role === 0) {
            toast.error("You don't have permission to do this.");
            return;
        }
        if (!form.clientDetail?.clientType) {
            toast.error("Client type is required");
            return;
        }

        if (!TAX_NUMBER_REGEX.test(String(form.taxNumber).replace(/\D/g, ""))) {
            toast.error("Tax ID Number has an invalid format");
            return;
        }

        if (form.clientDetail?.clientType === "individual") {
            form.clientDetail.pointOfContactName = "";
            form.clientDetail.pointOfContactEmail = "";
            form.clientDetail.secondaryPointOfContactName = "";
            form.clientDetail.secondaryPointOfContactEmail = "";
        } else if (form.clientDetail?.clientType === "business") {
            form.clientDetail.spouseName = "";
            form.clientDetail.spouseEmail = "";
        }

        const res = await updateClient({
            ...form,
            taxNumber: String(form.taxNumber ?? "").replace(/\D/g, ""),
            clientDetail: {
                ...form.clientDetail,
                spouseTaxNumber: String(
                    form.clientDetail?.spouseTaxNumber ?? ""
                ).replace(/\D/g, ""),
            },
        });
        if (res.success) {
            toast.success(res.message);
            navigate("/clients");
        } else {
            toast.error(res.message);
        }
    }

    const onChangeTeams = (newTeams) => {
        setForm({ ...form, teams: newTeams });
    };

    return (
        <section>
            <Toaster />
            <div className="flex h-screen overflow-hidden">
                {/* Sidebar */}
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                {/* Content area */}
                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    {/*  Site header */}
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <PageContainer
                        isLoading={loading}
                        loadingText="Loading client details..."
                    >
                        <main>
                            <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                                <div className="flex flex-col items-start max-w-2xl">
                                    <Breadcrumbs
                                        home="/clients"
                                        pages={[
                                            {
                                                name: "Edit client",
                                                href: `/clients/${params.id}`,
                                                current: true,
                                            },
                                        ]}
                                    />

                                    {/* Page header */}
                                    <div className="w-full mt-12 mb-8 sm:flex sm:justify-between sm:items-center">
                                        {/* Left: Title */}
                                        <div className="mb-4 sm:mb-0">
                                            <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                                Edit client
                                            </h1>
                                        </div>
                                    </div>

                                    <div className="relative w-full bg-white border rounded-sm shadow-lg border-slate-200">
                                        <header className="px-5 py-4 border-b border-slate-200">
                                            <h2 className="font-semibold text-slate-800">
                                                Details
                                            </h2>
                                        </header>

                                        <div className="px-5 py-4 space-y-5">
                                            <div>
                                                <label
                                                    htmlFor="email"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    Name
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        onChange={(e) =>
                                                            setForm({
                                                                ...form,
                                                                clientName:
                                                                    e.target.value,
                                                            })
                                                        }
                                                        type="text"
                                                        value={form.clientName}
                                                        className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label
                                                    htmlFor="email"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    Email
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        onChange={(e) =>
                                                            setForm({
                                                                ...form,
                                                                clientDetail: {
                                                                    ...form.clientDetail,
                                                                    clientEmail:
                                                                        e.target
                                                                            .value,
                                                                },
                                                            })
                                                        }
                                                        type="text"
                                                        value={
                                                            form.clientDetail
                                                                ?.clientEmail
                                                        }
                                                        className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label
                                                    htmlFor="email"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    Phone number
                                                </label>
                                                <div className="mt-1">
                                                    <ReactInput
                                                        value={form.phoneNumber}
                                                        onChange={(value) =>
                                                            setForm({
                                                                ...form,
                                                                phoneNumber: value,
                                                            })
                                                        }
                                                        format={templateFormatter(
                                                            "(xxx) xxx-xxxx"
                                                        )}
                                                        parse={templateParser(
                                                            "(xxx) xxx-xxxx",
                                                            parseDigit
                                                        )}
                                                        displayType="input"
                                                        type="text"
                                                        className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                    />
                                                </div>
                                            </div>
                                            <div className="pb-1">
                                                <label
                                                    htmlFor="email"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    Client type
                                                </label>
                                                <div className="mt-1">
                                                    <select
                                                        onChange={(e) =>
                                                            setForm({
                                                                ...form,
                                                                clientDetail: {
                                                                    ...form.clientDetail,
                                                                    clientType:
                                                                        e.target
                                                                            .value,
                                                                },
                                                            })
                                                        }
                                                        value={
                                                            form.clientDetail
                                                                ?.clientType
                                                        }
                                                        className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                    >
                                                        <option value="individual">
                                                            Individual
                                                        </option>
                                                        <option value="business">
                                                            Business
                                                        </option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div>
                                                <label
                                                    htmlFor="taxNumber"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    Tax ID Number
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        onChange={(e) =>
                                                            setForm({
                                                                ...form,
                                                                taxNumber:
                                                                    e.target.value
                                                                        .replace(
                                                                            /\D/g,
                                                                            ""
                                                                        )
                                                                        .slice(
                                                                            0,
                                                                            9
                                                                        ),
                                                            })
                                                        }
                                                        type="text"
                                                        value={formatTaxNumber({
                                                            clientType:
                                                                form.clientDetail
                                                                    ?.clientType,
                                                            taxNumber:
                                                                form.taxNumber,
                                                        })}
                                                        className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                    />
                                                </div>
                                            </div>

                                            <div className="pb-1">
                                                <label
                                                    htmlFor="email"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    Language
                                                </label>
                                                <div className="mt-1">
                                                    <select
                                                        onChange={(e) =>
                                                            setForm({
                                                                ...form,
                                                                clientDetail: {
                                                                    ...form.clientDetail,
                                                                    language:
                                                                        e.target
                                                                            .value,
                                                                },
                                                            })
                                                        }
                                                        value={
                                                            form.clientDetail
                                                                .language
                                                        }
                                                        className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                    >
                                                        <option value="English">
                                                            English
                                                        </option>
                                                        <option value="Spanish">
                                                            Spanish
                                                        </option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="pb-1">
                                                <label
                                                    htmlFor="contactMethod"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    Preferred method of contact
                                                </label>
                                                <div className="mt-1">
                                                    <select
                                                        onChange={(e) =>
                                                            setForm({
                                                                ...form,
                                                                clientDetail: {
                                                                    ...form.clientDetail,
                                                                    contactMethod:
                                                                        e.target
                                                                            .value,
                                                                },
                                                            })
                                                        }
                                                        value={
                                                            form.clientDetail
                                                                .contactMethod
                                                        }
                                                        className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                    >
                                                        <option value="Email">
                                                            Email
                                                        </option>
                                                        <option value="Text">
                                                            Text
                                                        </option>
                                                        <option value="Phone Call">
                                                            Phone Call
                                                        </option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Status
                                                </label>
                                                <div className="mt-2">
                                                    {typeof form.isActive === "boolean" ? (
                                                        <ColoredBadge
                                                            colorClass={
                                                                form.isActive
                                                                    ? "bg-teal-500 text-white"
                                                                    : "bg-red-500 text-white"
                                                            }
                                                            text={form.isActive ? "Active" : "Inactive"}
                                                        />
                                                    ) : (
                                                        <span className="text-sm text-slate-500">Not assigned</span>
                                                    )}
                                                </div>
                                                <p className="mt-2 text-xs text-slate-500">
                                                    Status is automatically determined by the client's services.
                                                </p>
                                            </div>

                                            {form.isActive === false && (
                                                <>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700">
                                                            Inactive Date
                                                        </label>
                                                        <div className="mt-1 text-sm text-slate-800">
                                                            {form.subscriptionCancellationDate || "—"}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700">
                                                            Inactive Reason
                                                        </label>
                                                        <div className="mt-1">
                                                            <input
                                                                type="text"
                                                                value={
                                                                    form.inactiveReason ??
                                                                    ""
                                                                }
                                                                onChange={(e) =>
                                                                    setForm({
                                                                        ...form,
                                                                        inactiveReason:
                                                                            e.target
                                                                                .value,
                                                                    })
                                                                }
                                                                className="w-full p-1 px-2 bg-white border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                            />
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                            <AssignTeamsListBox
                                                title={"Assigned teams *"}
                                                teams={teams}
                                                assignedTeams={form.teams}
                                                onChange={onChangeTeams}
                                            />
                                        </div>
                                    </div>

                                    <div className="relative w-full mt-6 bg-white border rounded-sm shadow-lg border-slate-200">
                                        <header className="px-5 py-4 border-b border-slate-200">
                                            <h2 className="font-semibold capitalize text-slate-800">
                                                {form.clientDetail?.clientType ==
                                                "individual"
                                                    ? "Spouse"
                                                    : "Business"}
                                            </h2>
                                        </header>

                                        {form.clientDetail?.clientType ==
                                        "individual" ? (
                                            <div>
                                                <div className="px-5 mt-5">
                                                    <label
                                                        htmlFor="email"
                                                        className="block text-sm font-medium text-gray-700"
                                                    >
                                                        {`Spouse Name`}
                                                    </label>
                                                    <div className="mt-1">
                                                        <input
                                                            onChange={(e) =>
                                                                setForm({
                                                                    ...form,
                                                                    clientDetail: {
                                                                        ...form.clientDetail,
                                                                        spouseName:
                                                                            e.target
                                                                                .value,
                                                                    },
                                                                })
                                                            }
                                                            type="text"
                                                            value={
                                                                form.clientDetail
                                                                    ?.spouseName
                                                            }
                                                            className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="px-5 mt-5">
                                                    <label
                                                        htmlFor="email"
                                                        className="block text-sm font-medium text-gray-700"
                                                    >
                                                        Spouse Tax ID Number
                                                    </label>
                                                    <div className="mt-1">
                                                        <input
                                                            onChange={(e) =>
                                                                setForm({
                                                                    ...form,
                                                                    clientDetail: {
                                                                        ...form.clientDetail,
                                                                        spouseTaxNumber:
                                                                            e.target.value
                                                                                .replace(
                                                                                    /\D/g,
                                                                                    ""
                                                                                )
                                                                                .slice(
                                                                                    0,
                                                                                    9
                                                                                ),
                                                                    },
                                                                })
                                                            }
                                                            type="text"
                                                            value={
                                                                    formatTaxNumber(
                                                                        {
                                                                            clientType:
                                                                                "INDIVIDUAL",
                                                                            taxNumber:
                                                                                form
                                                                                    .clientDetail
                                                                                    ?.spouseTaxNumber,
                                                                        }
                                                                    )
                                                                }
                                                            className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="px-5 pb-5 mt-5">
                                                    <label
                                                        htmlFor="email"
                                                        className="block text-sm font-medium text-gray-700"
                                                    >
                                                        Spouse Email
                                                    </label>
                                                    <div className="mt-1">
                                                        <input
                                                            onChange={(e) =>
                                                                setForm({
                                                                    ...form,
                                                                    clientDetail: {
                                                                        ...form.clientDetail,
                                                                        spouseEmail:
                                                                            e.target
                                                                                .value,
                                                                    },
                                                                })
                                                            }
                                                            type="text"
                                                            value={
                                                                form.clientDetail
                                                                    ?.spouseEmail
                                                            }
                                                            className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="px-5 mt-5">
                                                    <label
                                                        htmlFor="email"
                                                        className="block font-medium text-gray-700 text sm"
                                                    >
                                                        Name of main point of contact
                                                    </label>
                                                    <div className="mt-1">
                                                        <input
                                                            onChange={(e) =>
                                                                setForm({
                                                                    ...form,
                                                                    clientDetail: {
                                                                        ...form.clientDetail,
                                                                        pointOfContactName:
                                                                            e.target
                                                                                .value,
                                                                    },
                                                                })
                                                            }
                                                            type="text"
                                                            value={
                                                                form.clientDetail
                                                                    ?.pointOfContactName
                                                            }
                                                            className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="px-5 pb-5 mt-5">
                                                    <label
                                                        htmlFor="email"
                                                        className="block text-sm font-medium text-gray-700"
                                                    >
                                                        Email of main point of
                                                        contact
                                                    </label>
                                                    <div className="mt-1">
                                                        <input
                                                            onChange={(e) =>
                                                                setForm({
                                                                    ...form,
                                                                    clientDetail: {
                                                                        ...form.clientDetail,
                                                                        pointOfContactEmail:
                                                                            e.target
                                                                                .value,
                                                                    },
                                                                })
                                                            }
                                                            type="text"
                                                            value={
                                                                form.clientDetail
                                                                    ?.pointOfContactEmail
                                                            }
                                                            className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="px-5 mt-5">
                                                    <label
                                                        htmlFor="email"
                                                        className="block text-sm font-medium text-gray-700"
                                                    >
                                                        Name of secondary point of
                                                        contact
                                                    </label>
                                                    <div className="mt-1">
                                                        <input
                                                            onChange={(e) =>
                                                                setForm({
                                                                    ...form,
                                                                    clientDetail: {
                                                                        ...form.clientDetail,
                                                                        secondaryPointOfContactName:
                                                                            e.target
                                                                                .value,
                                                                    },
                                                                })
                                                            }
                                                            type="text"
                                                            value={
                                                                form.clientDetail
                                                                    ?.secondaryPointOfContactName
                                                            }
                                                            className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="px-5 pb-5 mt-5">
                                                    <label
                                                        htmlFor="email"
                                                        className="block text-sm font-medium text-gray-700"
                                                    >
                                                        Email of secondary point of
                                                        contact
                                                    </label>
                                                    <div className="mt-1">
                                                        <input
                                                            onChange={(e) =>
                                                                setForm({
                                                                    ...form,
                                                                    clientDetail: {
                                                                        ...form.clientDetail,
                                                                        secondaryPointOfContactEmail:
                                                                            e.target
                                                                                .value,
                                                                    },
                                                                })
                                                            }
                                                            type="text"
                                                            value={
                                                                form.clientDetail
                                                                    ?.secondaryPointOfContactEmail
                                                            }
                                                            className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="relative w-full mt-6 bg-white border rounded-sm shadow-lg border-slate-200">
                                        <header className="px-5 py-4 border-b border-slate-200">
                                            <h2 className="font-semibold text-slate-800">
                                                Address
                                            </h2>
                                        </header>

                                        <div className="px-5 mt-5">
                                            <label
                                                htmlFor="email"
                                                className="block text-sm font-medium text-gray-700"
                                            >
                                                Street address
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            address: {
                                                                ...form.address,
                                                                street: e.target
                                                                    .value,
                                                            },
                                                        })
                                                    }
                                                    type="text"
                                                    value={form.address?.street}
                                                    className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                        <div className="px-5 mt-5">
                                            <label
                                                htmlFor="email"
                                                className="block text-sm font-medium text-gray-700"
                                            >
                                                City
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            address: {
                                                                ...form.address,
                                                                city: e.target.value,
                                                            },
                                                        })
                                                    }
                                                    type="text"
                                                    value={form.address?.city}
                                                    className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                        <div className="px-5 mt-5">
                                            <label
                                                htmlFor="email"
                                                className="block text-sm font-medium text-gray-700"
                                            >
                                                State/Province
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            address: {
                                                                ...form.address,
                                                                stateProvince:
                                                                    e.target.value,
                                                            },
                                                        })
                                                    }
                                                    type="text"
                                                    value={
                                                        form.address?.stateProvince
                                                    }
                                                    className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                        <div className="px-5 mt-5">
                                            <label
                                                htmlFor="email"
                                                className="block text-sm font-medium text-gray-700"
                                            >
                                                Postal Code
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            address: {
                                                                ...form.address,
                                                                postalCode:
                                                                    e.target.value,
                                                            },
                                                        })
                                                    }
                                                    type="text"
                                                    value={form.address?.postalCode}
                                                    className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                        <div className="px-5 pb-3 mt-5">
                                            <label
                                                htmlFor="email"
                                                className="block text-sm font-medium text-gray-700"
                                            >
                                                Country
                                            </label>
                                            <div className="mt-1 mb-2">
                                                <select
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            address: {
                                                                ...form.address,
                                                                country:
                                                                    e.target.value,
                                                            },
                                                        })
                                                    }
                                                    value={form.address?.country}
                                                    className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                >
                                                    <option value="United States">
                                                        United States
                                                    </option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-end w-full">
                                        <button
                                            onClick={() => save()}
                                            type="submit"
                                            className="z-50 inline-flex justify-center px-4 py-2 mt-4 ml-3 text-sm font-medium text-white bg-teal-500 border border-transparent rounded-sm shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-0"
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </main>
                    </PageContainer>
                </div>
            </div>
        </section>
    );
}
export default CreateClientForm;
